import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ViewChild} from '@angular/core';
import {ProjectService} from '../shared/services/project.service';
import {NavigationEnd, Router} from '@angular/router';
import {ProjectId} from '../shared/types/ids';
import {isUUID} from '../shared/utils/utils';
import {UserCustomizationService} from '@webmate-web-core/shared/services/user-customization.service';
import {take} from 'rxjs/operators';
import {ProjectInfo} from '@webmate-web-core/shared/types/projectInfo';
import {Popover} from 'primeng/popover';

@Component({
    selector: 'wm-projectchooser',
    styleUrls: ['./projectchooser.component.less'],
    template: `
        <div class="wm-projectchooser-dropdown" #dropdowncontainer (click)="openMenu(op, $event)"
             data-testing-id="projectchooser-dropdown">
            <div class="wm-projectchooser-dropdown-name">{{ getCurrentDisplayString() }}</div>
            <div class="wm-projectchooser-dropdown-caret">
                <fa-icon size="lg" icon="caret-down" aria-hidden="true"></fa-icon>
            </div>
        </div>
        <p-popover appendTo="body" [styleClass]="'navbar-dropdown project-chooser'" #op (onShow)="menuOpened()">
            <div class="filter-container">
                <p-iconfield>
                    <p-inputicon>
                        <fa-icon icon="search" aria-hidden="true"></fa-icon>
                    </p-inputicon>
                    <input #filterInput pInputText [(ngModel)]="projectFilter"
                           (keyup.enter)="enterPressed() && op.toggle($event)" (keydown.escape)="op.toggle($event)"
                           (keydown.arrowDown)="arrowDown()" (keydown.arrowUp)="arrowUp()">
                </p-iconfield>
            </div>
            @for (project of getFilteredRecentProjects(); track project; let idx = $index; let isLast = $last) {
                <a
                    class="wm-projectchooser-dropdown-entry"
                    [ngClass]="{'chosen': currentProject === project.id, 'highlit': idx === selectedIndex, 'divider': isLast}"
                    (click)="updateAndNavigateToProject(project.id); op.hide()"
                    attr.data-testing-id="projectchooser-entry-{{project.id}}">
                    {{ project.name }}
                </a>
            }
            @for (project of getFilteredProjects(); track project; let idx = $index) {
                <a
                    class="wm-projectchooser-dropdown-entry"
                    [ngClass]="{'chosen': currentProject === project.id, 'highlit': idx + getFilteredRecentProjects().length === selectedIndex}"
                    (click)="updateAndNavigateToProject(project.id); op.hide()"
                    attr.data-testing-id="projectchooser-entry-{{project.id}}">
                    {{ project.name }}
                </a>
            }
        </p-popover>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectChooserComponent {

    readonly LIMIT = 10;

    currentProject: string = '';
    rawProjects: ProjectInfo[] = [];
    sortedProjects: ProjectInfo[] = [];
    recentProjects: ProjectInfo[] = [];

    projectFilter = '';

    selectedIndex = -1;

    @ViewChild('filterInput', {static: false}) filterInput: ElementRef;

    constructor(private projectService: ProjectService, private router: Router, private userCustomizations: UserCustomizationService, private cdRef: ChangeDetectorRef) {
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd && event.urlAfterRedirects) {
                this.extractCurrentProjectFromUrl(event.urlAfterRedirects);
                this.cdRef.markForCheck();
            }
        });
    }

    ngOnInit() {
        this.projectService.getProjectInfosForCurrentUser().subscribe(projectArr => {
            this.rawProjects = projectArr.sort((p1, p2) => p1.name.localeCompare(p2.name));
            this.extractCurrentProjectFromUrl(this.router.routerState.snapshot.url);
            this.cdRef.markForCheck();
        });
    }

    updateAndNavigateToProject(projectIdOrAll: ProjectId) {
        this.currentProject = projectIdOrAll;
        this.router.navigate(['/projects', this.currentProject]);
    }

    getCurrentDisplayString(): string {
        if (this.currentProject === '') {
            return '';
        } else if (this.currentProject === 'not-found') {
            return 'Project Not Found';
        } else if (this.currentProject === 'overview') {
            return 'Project Overview';
        } else if (this.currentProject === 'administration') {
            return 'Administration';
        } else if (this.currentProject === 'user-profile') {
            return 'User Profile';
        }
        return this.rawProjects.find((project) => project.id === this.currentProject)?.name || 'Unknown project';
    }

    private extractCurrentProjectFromUrl(url: string) {
        try {
            let segments = url.split('/');
            if (segments[1] === 'projects' && isUUID(segments[2])) {
                this.currentProject = segments[2];
            } else if (segments[1] === 'administration') {
                this.currentProject = 'administration';
            } else if (segments[2] === 'overview') {
                this.currentProject = 'overview';
            } else if (segments[1] === 'user-profile') {
                this.currentProject = 'user-profile';
            } else {
                this.currentProject = 'not-found';
            }
        } catch (e) {
            this.currentProject = 'not-found';
        }
    }

    getFilteredRecentProjects() {
        if (this.projectFilter.length > 0) {
            return this.recentProjects.filter(p => p.name.toLowerCase().includes(this.projectFilter.toLowerCase()) || p.id.includes(this.projectFilter.toLowerCase()));
        }
        return this.recentProjects;
    }

    getFilteredProjects() {
        if (this.projectFilter.length > 0) {
            return this.sortedProjects.filter(p => p.name.toLowerCase().includes(this.projectFilter.toLowerCase()) || p.id.includes(this.projectFilter.toLowerCase()));
        }
        return this.sortedProjects;
    }

    openMenu(op: Popover, event: any, container?: any) {
        if (this.rawProjects.length >= this.LIMIT) {
            this.userCustomizations.getMostRecentProjects().pipe(take(1)).subscribe(recentIds => {
                this.sortedProjects = [];
                this.recentProjects = recentIds
                    .map(id => this.rawProjects.find(x => x.id === id))
                    .filter(optProjectInfo => !!optProjectInfo)
                    .map(optProjectInfo => optProjectInfo as ProjectInfo);
                this.sortedProjects = this.rawProjects.filter(p => !recentIds.includes(p.id));
                if (container) {
                    op.toggle(event, container);
                } else {
                    op.toggle(event);
                }
            });
        } else {
            this.sortedProjects = [...this.rawProjects];
            if (container) {
                op.toggle(event, container);
            } else {
                op.toggle(event);
            }
        }
    }

    menuOpened() {
        this.projectFilter = '';
        this.selectedIndex = -1;
        if (this.filterInput?.nativeElement) {
            setTimeout(() => {
                this.filterInput.nativeElement.focus();
            });
        }
    }

    enterPressed() {
        let p = [...this.getFilteredRecentProjects(), ...this.getFilteredProjects()];
        if (p.length === 1) {
            this.updateAndNavigateToProject(p[0].id);
            return true;
        } else if (this.selectedIndex > -1 && !!p[this.selectedIndex]) {
            this.updateAndNavigateToProject(p[this.selectedIndex].id);
            return true;
        }
        return false;
    }

    arrowDown() {
        this.selectedIndex++;
        if (this.selectedIndex >= (this.getFilteredProjects().length + this.getFilteredRecentProjects().length)) {
            this.selectedIndex = 0;
        }
    }

    arrowUp() {
        this.selectedIndex--;
        if (this.selectedIndex < 0) {
            this.selectedIndex = (this.getFilteredProjects().length + this.getFilteredRecentProjects().length) - 1;
        }
    }
}
